import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import moment from 'moment';
import Pagination from '../../pagination';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { useDispatch } from 'react-redux';
import { setSelectedSection } from '../../../../redux/actions/sideBarAction';
import TableLoadingSkeleton from '../../../Utils/LoadingAnimations/TableLoadingSkeleton';
import LazyImage from '../Components/imageComponent';
import { globalEnv } from '../../../../config/baseConfig';
import { setSearchPlaceholderAction } from '../../../../redux/actions/searchBarAction';
import { HeaderItem, sortingOrder } from '../Components/common';
import { useTypedSelector } from '../../../../hooks/useTypeSelector';
import { DataNotFoundAnimation } from '../../../Utils/ErrorAnimations/dataNotFoundAnim';
import { axiosInstance } from '../../../../config/axiosInstance';
import placeholder from '../../../../assets/images/placeholder.png';
import ParentModal from '../../../Utils/Components/parentModal';
import { ReferredExtensionUserListModalContent } from './modals/ReferredExtensionUsersModal';
import { ReferredExtensionDevicesModalContent } from './modals/ReferredExtensionDevicesModal';

export enum ModalEnum {
	ACTIVE_USERS = 'users',
	ALL_USERS = 'all_users',
	ALL_DEVICES = 'all_devices'
}

export default function ReferredExtensionsTable() {
	const queryClient = useQueryClient();
	const [page, setPage] = useState(1);
	const [sortOrder, setSortOrder] = useState(sortingOrder.DESCENDING);
	const [selectedHeader, setSelectedHeader] = useState('updatedAt');
	const [isShowingUsersModal, setIsShowingUsersModal] = useState(false);
	const [selectedReferredExtension, setSelectedReferredExtension] = useState<
		// eslint-disable-next-line prettier/prettier
		{ devices: string[], extensionId: string; extensionName: string; referralCode: string; modalType: ModalEnum }
		| undefined
	>(undefined);

	const { searchQuery }: { searchQuery: string } = useTypedSelector(
		(state) => state.searchbar
	);

	const [dimensions, setDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight
	});

	const { ACCESS_TOKEN } = useTypedSelector((state) => state.auth);

	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(setSelectedSection('Referred Extnesions Documents'));
		dispatch(setSearchPlaceholderAction('Search for referred extensions'));
	}, []);

	useEffect(() => {
		function handleResize() {
			setDimensions({
				width: window.innerWidth,
				height: window.innerHeight
			});
		}
		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	const { data, isError, isFetching, isLoading } = useQuery(
		['referredExtensions', page, selectedHeader, sortOrder, searchQuery],
		() => fetchReferredExtensions(page),
		{ keepPreviousData: false, staleTime: 500, refetchOnWindowFocus: false }
	);

	// Prefetch the next page!
	useEffect(() => {
		if (data?.hasMore) {
			queryClient.prefetchQuery(['referredExtensions', page + 1], () =>
				fetchReferredExtensions(page + 1)
			);
		}
	}, [data, page, queryClient]);

	async function fetchReferredExtensions(page: number) {
		const config = {
			headers: {
				Authorization: 'Bearer ' + ACCESS_TOKEN
			}
		};
		const { data } = await axiosInstance.get(
			globalEnv.BASE_URL +
				`/referred-signup/get-referred-all?page=${page}&sortby=${selectedHeader}&order=${sortOrder}${
					searchQuery !== '' ? '&filter=' + searchQuery : ''
				}`,
			config
		);
		return data;
	}

	const TableHeaders = () => {
		return (
			<tr>
				<HeaderItem
					name="Referral code"
					slugName="name"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<HeaderItem
					name="Extension"
					slugName="extensionName"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				{/* <HeaderItem name="Users count" slugName="usersCount" isCenter={true} /> */}

				<HeaderItem
					name="Devices count"
					slugName="devicesCount"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<HeaderItem
					name="Users count"
					slugName="usersCount"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<HeaderItem
					name="Active users count"
					slugName="activeUsersCount"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<HeaderItem
					name="Creation date"
					slugName="createdAt"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>
				<HeaderItem
					name="Last update date"
					slugName="updatedAt"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>
			</tr>
		);
	};

	return (
		<section>
			{selectedReferredExtension != null && (
				<ParentModal
					visible={isShowingUsersModal}
					setModalOpen={setIsShowingUsersModal}
					isChanged={false}
					setIsChanged={() => {
						return;
					}}
					isWarnModalOpen={false}
					setIsWarnModalOpen={() => {
						return;
					}}
				>
					{selectedReferredExtension.modalType === ModalEnum.ALL_DEVICES ? (
						<ReferredExtensionDevicesModalContent
							devicesList={selectedReferredExtension.devices}
						/>
					) : (
						<ReferredExtensionUserListModalContent
							extensionId={selectedReferredExtension.extensionId}
							referralCode={selectedReferredExtension.referralCode}
							modalType={selectedReferredExtension.modalType}
						/>
					)}
				</ParentModal>
			)}

			<div className="p-3">
				<div>
					<div className="overflow-x-auto ">
						<table className="table-auto w-full">
							{isFetching || isLoading || isError ? (
								<></>
							) : (
								<thead className="text-xs font-semibold uppercase dark:text-primary-text-dark text-primary-text-light ">
									<TableHeaders />
								</thead>
							)}
							<tbody className="text-sm divide-y divide-gray-300 dark:divide-gray-600 transition-all duration-500">
								{isLoading || isFetching || isError ? (
									<></>
								) : (
									data.referredExtensions.map((item: any) => (
										<TableData
											key={item.extension._id}
											setSelectedReferredExtension={
												setSelectedReferredExtension
											}
											setIsShowingUsersModal={setIsShowingUsersModal}
											item={item}
										/>
									))
								)}
							</tbody>
						</table>
					</div>
				</div>
				{isFetching || isLoading ? (
					<div>
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
					</div>
				) : isError ? (
					<div className="h-full ml-0 ">
						<div className=" flex flex-col mx-auto my-auto text-center align-middle overflow-clip h-full ">
							<div className="mx-auto my-auto self-center">
								<div className="min-h-16">
									<DataNotFoundAnimation height={dimensions.height / 1.5} />
								</div>
								<h3 className="lg:text-2xl text-xl font-bold dark:text-gray-200 text-gray-700 -mt-8">
									{"Couldn't find the data you're looking for!"}
								</h3>
							</div>
						</div>
					</div>
				) : (
					<Pagination
						itemsPerPage={data.meta.limit}
						totalCount={data.meta.total}
						totalPages={data.meta.pages}
						currentPage={page}
						setPage={setPage}
					/>
				)}
			</div>

			{/* <ReactQueryDevtools initialIsOpen /> */}
		</section>
	);
}

type tableDateType = {
	item: any;
	setIsShowingUsersModal: (isShowingUsersModal: boolean) => void;
	setSelectedReferredExtension: (selectedReferredExtension: {
		devices: string[];
		extensionId: string;
		extensionName: string;
		referralCode: string;
		modalType: ModalEnum;
	}) => void;
};

const TableData = ({
	item,
	setSelectedReferredExtension: setSelectedReferredExtension,
	setIsShowingUsersModal
}: tableDateType) => {
	return (
		<tr>
			<td className="p-2 whitespace-nowrap">
				<div className="text-left font-medium dark:text-gray-400">
					{item._id.referralCode != null ? item._id.referralCode : 'N/A'}
				</div>
			</td>
			<td className="col-span-1 p-2 break-all w-60">
				<div className="flex items-center">
					<div className="w-10 h-10 flex justify-center flex-shrink-0 mr-2 sm:mr-3">
						<LazyImage
							photo={
								item.extension.topic.dailyImage.url_low_res != null
									? item.extension.topic.dailyImage.url_low_res
									: placeholder
							}
							style={
								'block object-cover object-center w-full aspect-[1/1] rounded-lg'
							}
						/>
					</div>
					<div className="font-medium text-gray-800 dark:text-white">
						{item.extension.name}
					</div>
				</div>
			</td>

			{/* <td className="col-span-1 p-2 break-all">
				<div className="text-center dark:text-gray-400">{item.usersCount}</div>
			</td> */}
			<td
				className={`col-span-1 p-2 break-all ${
					item.devicesCount > 0 && 'cursor-pointer'
				}`}
				onClick={() => {
					if (item.devicesCount > 0) {
						setIsShowingUsersModal(true);
						setSelectedReferredExtension({
							devices: item.devices,
							extensionId: item.extension._id,
							extensionName: item.extension.name,
							referralCode: item._id.referralCode,
							modalType: ModalEnum.ALL_DEVICES
						});
					}
				}}
			>
				<div className="text-center dark:text-gray-400">
					{item.devicesCount}
				</div>
			</td>
			<td
				onClick={() => {
					if (item.usersCount > 0) {
						setIsShowingUsersModal(true);
						setSelectedReferredExtension({
							devices: item.devices,
							extensionId: item.extension._id,
							extensionName: item.extension.name,
							referralCode: item._id.referralCode,
							modalType: ModalEnum.ALL_USERS
						});
					}
				}}
				className={`col-span-1 p-2 break-all ${
					item.usersCount > 0 && 'cursor-pointer'
				}`}
			>
				<div className="text-center dark:text-gray-400">{item.usersCount}</div>
			</td>

			<td
				onClick={() => {
					if (item.usersCount > 0) {
						setIsShowingUsersModal(true);
						setSelectedReferredExtension({
							devices: item.devices,
							extensionId: item.extension._id,
							extensionName: item.extension.name,
							referralCode: item._id.referralCode,
							modalType: ModalEnum.ACTIVE_USERS
						});
					}
				}}
				className={`col-span-1 p-2 break-all ${
					item.activeUsersCount > 0 && 'cursor-pointer'
				}`}
			>
				<div className="text-center dark:text-gray-400">
					{item.activeUsersCount}
				</div>
			</td>

			<td className="p-2 whitespace-nowrap">
				<div className="text-center font-medium dark:text-gray-400">
					{moment(item.createdAt).format('MM/DD/YYYY - HH:mm')}
				</div>
			</td>
			<td className="p-2 whitespace-nowrap">
				<div className="text-center font-medium dark:text-gray-400">
					{moment(item.updatedAt).format('MM/DD/YYYY - HH:mm')}
				</div>
			</td>
		</tr>
	);
};
